import type { FallbackProps } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import Borders from '@dynatrace/strato-design-tokens/borders';
import { CriticalIcon, ResetIcon } from '@dynatrace/strato-icons';

import { errorFallbackMessages } from './messages.js';
import { Button } from '../../../buttons/button/Button.js';
import { Container } from '../../../layouts/container/Container.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Text } from '../../../typography/text/Text.js';

/**
 * Fallback component shown in place of a component when an unhandled exception
 * is thrown, so the host app remains functional. Includes a reset button to allow
 * graceful recovery.
 * @internal
 */
export const ErrorFallback = ({ resetErrorBoundary }: FallbackProps) => {
  return (
    <Container
      padding={2}
      paddingLeft={4}
      color="critical"
      style={{
        borderRadius: Borders.Radius.Container.Subdued,
      }}
    >
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={4}
      >
        <CriticalIcon />
        <Text
          textStyle="base-emphasized"
          style={{
            flex: 1,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          <FormattedMessage {...errorFallbackMessages.errorTitleMessage} />
        </Text>
        <Button
          variant="emphasized"
          color="critical"
          onClick={resetErrorBoundary}
          size="condensed"
          style={{ borderRadius: Borders.Radius.Field.Subdued }}
        >
          <Button.Prefix>
            <ResetIcon />
          </Button.Prefix>
          <FormattedMessage {...errorFallbackMessages.recoverStateButtonText} />
        </Button>
      </Flex>
    </Container>
  );
};
